import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useClinica } from '@/contexts/ClinicaContext';
import { useAuthUser } from '@/hooks/useAuthUser';

/**
 * Hook para verificar permissões baseadas no plano da clínica
 * 
 * REGRAS DE NEGÓCIO POR PLANO:
 * 
 * PMK: Totalmente restrito (só com override admin)
 * - canUseAI: false (só com override)
 * - canGenerateReports: false (só com override)
 * - canConfigureAI: false (só com override)
 * - canViewAISettings: false (só com override)
 * - monthlyReportLimit: 0
 * 
 * FREE: Conta criada, mas ainda sem acesso ao produto até concluir o pagamento
 * 
 * BASIC: Recursos intermediários
 * - canUseAI: false
 * - canGenerateReports: true (2 por mês)
 * - canConfigureAI: false
 * - canViewAISettings: true (view-only)
 * - monthlyReportLimit: 2
 * 
 * PROFESSIONAL: Todos os recursos
 * - canUseAI: true
 * - canGenerateReports: true (4 por mês)
 * - canConfigureAI: true
 * - canViewAISettings: true
 * - monthlyReportLimit: 4
 * 
 * ADMIN: Acesso total sempre
 */

interface PlanPermissions {
  canUseAI: boolean; // Pode usar chat IA
  canGenerateReports: boolean; // Pode gerar relatórios de IA
  canConfigureAI: boolean; // Pode editar configurações de IA
  canViewAISettings: boolean; // Pode visualizar configurações (read-only para alguns planos)
  monthlyReportLimit: number; // Limite de relatórios por mês
  isPMKPlan: boolean; // É plano PMK
  isAdmin: boolean; // É administrador
  hasAIOverride: boolean; // Admin liberou override de IA
  planName: string; // Nome do plano atual
  canManageCustomLinks: boolean;
  canUseFollowup: boolean; // Follow-up automatico liberado apenas para Premium, PMK e admin
  canUseChatAnalysis: boolean; // Análise de chats — Pro (1/mês), Premium (2/mês), PMK (1/mês), admin (ilimitado)
  analysisQuotaPerMonth: number; // Cota mensal de análises (-1 = ilimitado)
}

/**
 * Interface para as features do plano (estrutura esperada no JSONB)
 */
interface PlanFeatures {
  ai_chat?: boolean;
  ai_reports?: boolean;
  ai_suggestions?: boolean;
  ai_settings?: boolean;
  ai_view_settings?: boolean;
  monthly_report_limit?: number;
  basic_reports?: boolean;
  kanban?: boolean;
  follow_up?: boolean;
  chat?: boolean;
  dashboard?: boolean;
  custom_links?: boolean;
}

const normalizePlanName = (planName?: string | null) => {
  // Normaliza o nome do plano antes das regras para evitar bloqueios indevidos
  // quando o banco retorna "Premium", "premium " ou outra variação simples.
  return String(planName || 'free').trim().toLowerCase();
};

interface RegistroCanalPermissoesPlano {
  canal: ReturnType<typeof supabase.channel>;
  consumidores: number;
}

// O mesmo hook aparece no gate de assinatura e nas páginas internas. A versão atual
// do Supabase reutiliza canais com o mesmo nome e rejeita novos callbacks após subscribe().
// Este registro mantém uma única inscrição por clínica enquanto houver consumidores ativos.
const canaisPermissoesPlano = new Map<string, RegistroCanalPermissoesPlano>();

function liberarCanalPermissoesPlano(clinicaId: string): void {
  const registro = canaisPermissoesPlano.get(clinicaId);
  if (!registro) return;

  registro.consumidores -= 1;
  if (registro.consumidores > 0) return;

  // Retira o registro quando não há consumidores; removeChannel fica responsável
  // por concluir o encerramento assíncrono da conexão WebSocket.
  canaisPermissoesPlano.delete(clinicaId);
  console.log('🧹 [usePlanPermissions] Removendo Real-Time Subscription');
  void supabase.removeChannel(registro.canal);
}

export const usePlanPermissions = (): PlanPermissions & { isLoading: boolean; error: any } => {
  // Clínica ativa + loading do contexto: o gate de billing depende de NÃO assumir "free"
  // enquanto a clínica ainda não chegou (evita flash/redirecionamento errado para /billing).
  const { clinicaAtiva, isLoading: clinicaContextLoading } = useClinica();

  const { user } = useAuthUser();

  // Buscar dados da assinatura e plano atual
  const { data: subscriptionData, isLoading: subscriptionLoading, error } = useQuery({
    queryKey: ['subscription-permissions', clinicaAtiva?.id],
    queryFn: async () => {
      if (!clinicaAtiva?.id) return null;

      console.log('🔍 [usePlanPermissions] Buscando dados da assinatura para clínica:', clinicaAtiva.id);

      // Tabela: subscriptions | Operação: SELECT | Campos: assinatura e plano relacionado.
      // Filtro: clínica atual e status active | Uso: liberar recursos somente após pagamento ativo.
      // Risco: não lê dados financeiros sensíveis nem credenciais do Stripe.
      const { data: subscription, error: subError } = await supabase
        .from('subscriptions')
        .select(`
          *,
          plan:plans(*)
        `)
        .eq('clinica_id', clinicaAtiva.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      console.log('🔍 [usePlanPermissions] Resultado da busca de assinatura:', {
        data: subscription,
        error: subError,
        clinicaId: clinicaAtiva.id
      });

      if (subError && subError.code !== 'PGRST116') {
        console.error('❌ [usePlanPermissions] Erro ao buscar subscription:', subError);
        throw subError;
      }

      // Se não tem assinatura, buscar plano diretamente da tabela clinicas
      if (!subscription) {
        console.log('📋 [usePlanPermissions] Sem assinatura, buscando plano_atual da clínica');

        // Buscar o plano diretamente da tabela clinicas
        const { data: clinicaData, error: clinicaError } = await supabase
          .from('clinicas')
          .select('plano_atual, plano_contratado')
          .eq('id', clinicaAtiva.id)
          .single();

        if (clinicaError) {
          console.error('❌ [usePlanPermissions] Erro ao buscar clínica:', clinicaError);
        }

        const planName = clinicaData?.plano_atual || clinicaData?.plano_contratado || 'free';
        console.log('📋 [usePlanPermissions] Plano da clínica:', planName);

        // Se temos um plano na tabela clinicas, buscar o plano correspondente
        if (planName && planName !== 'free') {
          const planNameNormalizado = normalizePlanName(planName);
          const { data: plan } = await supabase
            .from('plans')
            .select('*')
            .eq('name', planNameNormalizado)
            .single();

          if (plan) {
            console.log('✅ [usePlanPermissions] Plano encontrado via clinicas.plano_atual:', plan.name);
            return { plan };
          }

          // Mesmo sem linha em `plans`, respeita o plano da clínica (ex.: pmk setado no admin).
          // Antes caía em free e o gate mandava para /billing indevidamente.
          console.log('📋 [usePlanPermissions] Plano da clínica sem match em plans; usando nome da clínica:', planNameNormalizado);
          return {
            plan: {
              name: planNameNormalizado,
              display_name: planNameNormalizado.toUpperCase(),
              features: {}
            }
          };
        }

        // Fallback para free só quando a clínica realmente não tem plano pago/parceria
        return {
          plan: {
            name: 'free',
            display_name: 'Free',
            features: {
              ai_chat: false,
              ai_reports: true,
              ai_settings: false,
              ai_view_settings: true,
              monthly_report_limit: 1
            }
          }
        };
      }

      console.log('✅ [usePlanPermissions] Dados da assinatura encontrados:', {
        planName: subscription.plan?.name,
        status: subscription.status,
        features: subscription.plan?.features
      });

      return subscription;
    },
    enabled: !!clinicaAtiva?.id,
    /**
     * IMPORTANTE (plano/assinatura):
     * 
     * Quando o admin muda o plano de uma clínica, o usuário da clínica pode entrar em seguida
     * e ainda ver restrições “antigas” se a query estiver cacheada.
     * 
     * Por isso, aqui nós forçamos refetch ao montar e deixamos o staleTime = 0.
     * Assim, um simples F5 já puxa o plano atualizado.
     */
    staleTime: 0,
    gcTime: 1000 * 60 * 10, // 10 minutos

    // Sempre buscar ao montar (evita “plano antigo” após troca no admin)
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  // Verificar se usuário é admin
  const { data: isAdmin, isLoading: adminLoading } = useQuery({
    queryKey: ['user-admin-check', user?.id],
    queryFn: async () => {
      if (!user?.id) return false;

      const { data, error } = await supabase
        .from('user_profiles')
        .select('profile_type')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.warn('⚠️ [usePlanPermissions] Erro ao verificar admin:', error);
        return false;
      }

      return data?.profile_type === 'admin';
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 30, // 30 minutos
  });

  const queryClient = useQueryClient();

  // 🔄 REAL-TIME SUBSCRIPTION: Detectar mudanças de plano automaticamente
  useEffect(() => {
    if (!clinicaAtiva?.id) return;

    const clinicaId = clinicaAtiva.id;
    const registroExistente = canaisPermissoesPlano.get(clinicaId);
    if (registroExistente) {
      // Consumidores adicionais usam o canal já configurado; adicionar outro callback
      // ao mesmo canal inscrito derruba a árvore React nas versões atuais do Supabase.
      registroExistente.consumidores += 1;
      return () => liberarCanalPermissoesPlano(clinicaId);
    }

    console.log('📡 [usePlanPermissions] Configurando Real-Time Subscription para mudanças de plano');

    const channel = supabase
      .channel(`plan-changes-${clinicaId}`)
      // Detectar mudanças na tabela subscriptions
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `clinica_id=eq.${clinicaId}`
        },
        (payload) => {
          console.log('📡 [usePlanPermissions] Mudança detectada na subscription:', payload);
          queryClient.invalidateQueries({ queryKey: ['subscription-permissions', clinicaId] });
        }
      )
      // Detectar mudanças no plano da clínica
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'clinicas',
          filter: `id=eq.${clinicaId}`
        },
        (payload) => {
          const newData = payload.new as any;
          if (newData.plano_atual || newData.plano_contratado) {
            console.log('📡 [usePlanPermissions] Plano da clínica atualizado:', {
              plano_atual: newData.plano_atual,
              plano_contratado: newData.plano_contratado
            });
            queryClient.invalidateQueries({ queryKey: ['subscription-permissions', clinicaId] });
            queryClient.invalidateQueries({ queryKey: ['clinicaData', clinicaId] });
          }
        }
      )
      .subscribe((status) => {
        console.log('📡 [usePlanPermissions] Status da subscription:', status);
      });

    canaisPermissoesPlano.set(clinicaId, { canal: channel, consumidores: 1 });

    return () => liberarCanalPermissoesPlano(clinicaId);
  }, [clinicaAtiva?.id, queryClient]);

  // Clínica ainda carregando: NÃO tratar como "free".
  // Antes, isLoading virava false sem clínica e o gate mandava todo mundo para /billing
  // no primeiro render (PMK/pago incluídos). Só decide plano depois da clínica existir.
  const isLoading =
    clinicaContextLoading ||
    (!!clinicaAtiva?.id && (subscriptionLoading || adminLoading));

  // Calcular permissões baseadas no plano específico
  const permissions: PlanPermissions = {
    isAdmin: isAdmin || false,
    isPMKPlan: normalizePlanName(subscriptionData?.plan?.name) === 'pmk',
    planName: subscriptionData?.plan?.name || 'free',
    hasAIOverride: clinicaAtiva?.ai_override_enabled || false,
    canUseAI: false,
    canGenerateReports: false,
    canConfigureAI: false,
    canViewAISettings: false,
    monthlyReportLimit: 0,
    canManageCustomLinks: false,
    canUseFollowup: false,
    canUseChatAnalysis: false,
    analysisQuotaPerMonth: 0,
  };

  // Admin sempre tem acesso total
  if (permissions.isAdmin) {
    console.log('👑 [usePlanPermissions] Usuário é admin - acesso total liberado');
    permissions.canUseAI = true;
    permissions.canGenerateReports = true;
    permissions.canConfigureAI = true;
    permissions.canViewAISettings = true;
    permissions.monthlyReportLimit = 999; // Ilimitado para admin
    permissions.canManageCustomLinks = true;
    permissions.canUseFollowup = true;
    permissions.canUseChatAnalysis = true;
    permissions.analysisQuotaPerMonth = -1; // ilimitado
  } else {
    // Verificar permissões específicas por plano
    const planName = normalizePlanName(permissions.planName);
    const planFeatures = subscriptionData?.plan?.features as PlanFeatures || {};

    console.log('🎯 [usePlanPermissions] Aplicando regras para plano:', planName);

    switch (planName) {
      case 'pmk':
        console.log('🏥 [usePlanPermissions] Plano PMK - verificando override');
        const hasOverride = permissions.hasAIOverride;
        permissions.canUseAI = hasOverride;
        permissions.canGenerateReports = hasOverride;
        permissions.canConfigureAI = hasOverride;
        permissions.canViewAISettings = hasOverride;
        permissions.monthlyReportLimit = hasOverride ? 999 : 0;
        permissions.canManageCustomLinks = false; // PMK não tem acesso
        permissions.canUseFollowup = true;
        permissions.canUseChatAnalysis = true;
        permissions.analysisQuotaPerMonth = 1;
        console.log('🔧 [usePlanPermissions] Override ativo:', hasOverride);
        break;

      case 'free':
        console.log('💳 [usePlanPermissions] Conta sem assinatura - acesso depende do pagamento');
        permissions.canUseAI = false;
        permissions.canGenerateReports = false;
        permissions.canConfigureAI = false;
        permissions.canViewAISettings = false;
        permissions.monthlyReportLimit = 0;
        permissions.canManageCustomLinks = false;
        permissions.canUseFollowup = false;
        break;

      case 'trial':
        // Registros legados de trial permanecem sem acesso para impedir reativação gratuita acidental.
        console.log('💳 [usePlanPermissions] Trial legado - pagamento obrigatório');
        permissions.canUseAI = false;
        permissions.canGenerateReports = false;
        permissions.canConfigureAI = false;
        permissions.canViewAISettings = false;
        permissions.monthlyReportLimit = 0;
        permissions.canManageCustomLinks = false;
        permissions.canUseFollowup = false;
        break;

      case 'basic':
        console.log('📊 [usePlanPermissions] Plano Basic - recursos intermediários');
        permissions.canUseAI = true;
        permissions.canGenerateReports = true;
        permissions.canConfigureAI = true;
        permissions.canViewAISettings = true;
        permissions.monthlyReportLimit = 2;
        permissions.canManageCustomLinks = false;
        permissions.canUseFollowup = false;
        break;

      case 'pro':
        console.log('💼 [usePlanPermissions] Plano Pro - recursos avançados');
        permissions.canUseAI = true;
        permissions.canGenerateReports = true;
        permissions.canConfigureAI = true;
        permissions.canViewAISettings = true;
        permissions.monthlyReportLimit = 4;
        permissions.canManageCustomLinks = true;
        permissions.canUseFollowup = false;
        permissions.canUseChatAnalysis = true;
        permissions.analysisQuotaPerMonth = 1;
        break;

      case 'premium':
        console.log('💎 [usePlanPermissions] Plano Premium - todos os recursos');
        permissions.canUseAI = true;
        permissions.canGenerateReports = true;
        permissions.canConfigureAI = true;
        permissions.canViewAISettings = true;
        permissions.monthlyReportLimit = -1; // Ilimitado
        permissions.canManageCustomLinks = true;
        permissions.canUseFollowup = true;
        permissions.canUseChatAnalysis = true;
        permissions.analysisQuotaPerMonth = 2;
        break;

      default:
        console.log('❓ [usePlanPermissions] Plano desconhecido, usando configurações do features');
        // Para planos personalizados, usar configurações das features
        permissions.canUseAI = planFeatures.ai_chat || false;
        permissions.canGenerateReports = planFeatures.ai_reports || false;
        permissions.canConfigureAI = planFeatures.ai_settings || false;
        permissions.canViewAISettings = planFeatures.ai_view_settings || false;
        permissions.monthlyReportLimit = planFeatures.monthly_report_limit || 0;
        permissions.canManageCustomLinks = planFeatures.custom_links || false;
        permissions.canUseFollowup = false;
        break;
    }
  }

  console.log('🎯 [usePlanPermissions] Permissões finais calculadas:', permissions);

  return {
    ...permissions,
    isLoading,
    error,
  };
};
